import React from 'react';
import RootRenderer from './dynamic-components/RootRenderer';
import StepRenderer from './dynamic-components/StepRenderer';
import EpdMetadataRenderer from './dynamic-components/EpdMetadataRenderer';
import DocumentCollectionsRenderer from './dynamic-components/DocumentCollectionsRenderer';
import FieldPanelRenderer from './dynamic-components/FieldPanelRenderer';
import TwoColumnsRenderer from './dynamic-components/TwoColumnsRenderer';
import TextInputRenderer from './dynamic-components/TextInputRenderer';
import HtmlEditorInputRenderer from './dynamic-components/HtmlEditorInputRenderer';
import NumberInputRenderer from './dynamic-components/NumberInputRenderer';
import SelectInputRenderer from './dynamic-components/SelectInputRenderer';
import RadioListInputRenderer from './dynamic-components/RadioListInputRenderer';
import CheckboxInputRenderer from './dynamic-components/CheckboxInputRenderer';
import DateInputRenderer from './dynamic-components/DateInputRenderer';
import StandardsSelectorRenderer from './dynamic-components/StandardsSelectorRenderer';
import PcrSelectorRenderer from './dynamic-components/PcrSelectorRenderer';
import CpcrSelectorRenderer from './dynamic-components/CpcrSelectorRenderer';
import VerifierSelectInputRenderer from './dynamic-components/verifier-select-input/VerifierSelectInputRenderer';
import ProductsListRenderer from './dynamic-components/ProductsListRenderer';
import ManufacturingSitesListRenderer from './dynamic-components/ManufacturingSitesListRenderer';
import MaterialPropertiesListRenderer from './dynamic-components/material-properties-list/MaterialPropertiesListRenderer';
import LcaDatabasesListRenderer from './dynamic-components/lca-databases-list/LcaDatabasesListRenderer';
import AddableRowsGridRenderer from './dynamic-components/AddableRowsGridRenderer';
import ImagesListEditorRenderer from './dynamic-components/ImagesListEditorRenderer';
import FilesListRenderer from './dynamic-components/FilesListRenderer';
import SingleFileUploadRenderer from './dynamic-components/SingleFileUploadRenderer';
import PdfRootRenderer from './dynamic-components/PdfRootRenderer';
import PdfH1Renderer from './dynamic-components/PdfH1Renderer';
import LcaPractitionerSelectInputRenderer from './dynamic-components/lca-practitioner-select-input/LcaPractitionerSelectInputRenderer';
import PreVerifiedToolInputRenderer from './dynamic-components/pre-verified-tool-input/PreVerifiedToolInputRenderer';
import PublicationMetadataRenderer from './dynamic-components/PublicationMetadataRenderer';
import PublicationConfirmationRenderer from './dynamic-components/PublicationConfirmationRenderer';
import RegistrationCertificatePanelRenderer from './dynamic-components/RegistrationCertificatePanelRenderer';
import IlcdEpdBundlePanelRenderer from './dynamic-components/IlcdEpdBundlePanelRenderer';

interface Props {
  schema: any;
}

const SingleImageEditorRenderer: React.FC<any> = (props) => <ImagesListEditorRenderer {...props} single />;

const typenameToComponentsMap = new Map<string, React.FC<any>>([
  ['root', RootRenderer],
  ['step', StepRenderer],
  ['epdMetadata', EpdMetadataRenderer],
  ['documentCollections', DocumentCollectionsRenderer],
  ['fieldPanel', FieldPanelRenderer],
  ['twoColumns', TwoColumnsRenderer],
  ['textInput', TextInputRenderer],
  ['htmlEditorInput', HtmlEditorInputRenderer],
  ['numberInput', NumberInputRenderer],
  ['selectInput', SelectInputRenderer],
  ['radioListInput', RadioListInputRenderer],
  ['checkboxInput', CheckboxInputRenderer],
  ['preVerifiedToolInput', PreVerifiedToolInputRenderer],
  ['dateInput', DateInputRenderer],
  ['standardsSelector', StandardsSelectorRenderer],
  ['pcrSelector', PcrSelectorRenderer],
  ['cpcrSelector', CpcrSelectorRenderer],
  ['verifierSelectInput', VerifierSelectInputRenderer],
  ['lcaPractitionerSelectInput', LcaPractitionerSelectInputRenderer],
  ['publicationMetadata', PublicationMetadataRenderer],
  ['publicationConfirmation', PublicationConfirmationRenderer],
  ['registrationCertificatePanel', RegistrationCertificatePanelRenderer],
  ['ilcdEpdBundlePanel', IlcdEpdBundlePanelRenderer],
  ['productsList', ProductsListRenderer],
  ['manufacturingSitesList', ManufacturingSitesListRenderer],
  ['materialPropertiesList', MaterialPropertiesListRenderer],
  ['lcaDatabasesList', LcaDatabasesListRenderer],
  ['addableRowsGrid', AddableRowsGridRenderer],
  ['imagesListEditor', ImagesListEditorRenderer],
  ['imageEditor', SingleImageEditorRenderer],
  ['filesList', FilesListRenderer],
  ['singleFileUpload', SingleFileUploadRenderer],
  ['pdf-root', PdfRootRenderer],
  ['pdfH1', PdfH1Renderer],
]);

const DynamicContentRenderer: React.FC<Props> = ({ schema }) => {
  if (Array.isArray(schema)) {
    return schema.map((el, i) => <DynamicContentRenderer key={i} schema={el} />);
  }

  const typename = schema.__typename;
  if (!typename) {
    throw new Error(`Schema doesn't have __typename: "${JSON.stringify(schema)}"`);
  }

  const component = typenameToComponentsMap.get(typename);
  if (!component) {
    throw new Error(`Component for __typename "${typename}" doesn't exist.`);
  }

  return React.createElement(component, schema);
};

export default React.memo(DynamicContentRenderer);
